import React from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonOff as PersonOffIcon,
} from "@mui/icons-material";
import { useSiteVisit } from "../../contexts/SiteVisitContext";
import { useLoader } from "../../contexts/LoaderContext";
import CompleteNotes from "./CompleteNotes";
import RescheduleSiteVisit from "./RescheduleVisit";
import CancelVisit from "./CancelVisit";

const SiteVisitActions = ({
  siteVisit,
  mode = "list", // "list" = menu, "details" = buttons
  onSiteVisitUpdated,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openCompleteNotes, setOpenCompleteNotes] = React.useState(false);
  const [openRescheduleVisit, setOpenRescheduleVisit] = React.useState(false);
  const [openCancelVisit, setOpenCancelVisit] = React.useState(false);
  const [openMarkNoShowConfirm, setOpenMarkNoShowConfirm] = React.useState(false);
  const [markNoShowSubmitting, setMarkNoShowSubmitting] = React.useState(false);

  const { markNoShowSiteVisit } = useSiteVisit();
  const { showLoader, hideLoader } = useLoader();
  const open = Boolean(anchorEl);

  const isFinalState =
    siteVisit?.status === "completed" || siteVisit?.status === "cancelled";

  const isRescheduled = siteVisit?.status === "rescheduled";

  // Only show/enable three-dots menu for pending visits (API may return 'pending' from calendar_item or 'scheduled' from SiteVisit)
  const isPending =
    siteVisit?.status === "scheduled" || siteVisit?.status === "pending";

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    switch (action) {
      case "reschedule":
        setOpenRescheduleVisit(true);
        break;
      case "complete":
        setOpenCompleteNotes(true);
        break;
      case "cancel":
        setOpenCancelVisit(true);
        break;
      case "mark_no_show":
        setOpenMarkNoShowConfirm(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmMarkNoShow = async () => {
    const visitId = siteVisit?.site_visit_id ?? siteVisit?.id;
    if (!visitId) return;
    setMarkNoShowSubmitting(true);
    showLoader();
    try {
      await markNoShowSiteVisit(visitId);
      setOpenMarkNoShowConfirm(false);
      if (onSiteVisitUpdated) onSiteVisitUpdated();
    } catch (err) {
      console.error("Mark no show failed:", err);
    } finally {
      setMarkNoShowSubmitting(false);
      hideLoader();
    }
  };

  // Actions list
  const actions = [];
  
  if(mode === "details"){
    // For details page: Complete | Mark no show | Cancel
    actions.push(
      {
        key: "complete",
        label: "Complete",
        icon: <CheckCircleIcon fontSize="small" color="success" />,
      },
      {
        key: "mark_no_show",
        label: "Mark no show",
        icon: <PersonOffIcon fontSize="small" color="warning" />,
      },
      {
        key: "cancel",
        label: "Cancel",
        icon: <CancelIcon fontSize="small" color="error" />,
      }
    );
  } else {
    // List: Mark Complete | Mark no show | Cancel (no View Details)
    actions.push(
      {
        key: "complete",
        label: "Mark Complete",
        icon: <CheckCircleIcon fontSize="small" color="success" />,
      },
      {
        key: "mark_no_show",
        label: "Mark no show",
        icon: <PersonOffIcon fontSize="small" color="warning" />,
      },
      {
        key: "cancel",
        label: "Cancel",
        icon: <CancelIcon fontSize="small" color="error" />,
      }
    );
  }

  // If final state → no actions; if list mode and not pending → no actions
  const availableActions =
    isFinalState
      ? []
      : mode === "list" && !isPending
        ? []
        : actions;



  return (
    <>
      <Box>
        {mode === "list" ? (
          // Show menu only for pending (scheduled) visits
          isPending && (
            <>
              <IconButton onClick={handleOpen} sx={{ cursor: "pointer" }}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {availableActions.map((action) => (
                  <MenuItem
                    key={action.key}
                    onClick={() => {
                      handleClose();
                      handleAction(action.key);
                    }}
                  >
                    {action.icon}
                    <Box ml={1}>{action.label}</Box>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )
        ) : (
          // Show Buttons (only if not final state and not rescheduled)
          !isFinalState && !isRescheduled && (
            <Box display="flex" gap={1}>
              {availableActions.map((action) => (
                <Button
                  key={action.key}
                  variant="outlined"
                  size="small"
                  startIcon={action.icon}
                  onClick={() => handleAction(action.key)}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )
        )}
      </Box>

      {/* Modals */}
      <CompleteNotes
        open={openCompleteNotes}
        onClose={() => setOpenCompleteNotes(false)}
        siteVisit={siteVisit}
        onSuccess={() => {
          setOpenCompleteNotes(false);
        }}
      />

      <RescheduleSiteVisit
        open={openRescheduleVisit}
        onClose={() => setOpenRescheduleVisit(false)}
        siteVisit={siteVisit}
        onSuccess={() => {
          setOpenRescheduleVisit(false);
        }}
      />
      <CancelVisit
        open={openCancelVisit}
        onClose={() => setOpenCancelVisit(false)}
        siteVisit={siteVisit}
        onSuccess={() => {
          setOpenCancelVisit(false);
        }}
      />

      <Dialog
        open={openMarkNoShowConfirm}
        onClose={() => !markNoShowSubmitting && setOpenMarkNoShowConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark as no show</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark this site visit as no show? The visit will be cancelled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenMarkNoShowConfirm(false)}
            disabled={markNoShowSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmMarkNoShow}
            disabled={markNoShowSubmitting}
          >
            {markNoShowSubmitting ? "Marking…" : "Mark no show"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SiteVisitActions;
